import { cached } from "@/lib/ssrCache";

const SITEMAP_URL = "https://www.garden-review.co.uk/sitemap.xml";

export const SITE_ORIGIN = "https://www.garden-review.co.uk";
export const BROWSE_PAGE_SIZE = 200;

export interface BrowseEntry {
  path: string;
  label: string;
  section: string;
}

const SECTION_LABELS: Record<string, string> = {
  articles: "Articles & Guides",
  best: "Best-Of Guides",
  plants: "Plant Guides",
  category: "Categories",
  brand: "Brands",
  advice: "Advice",
  reviews: "Reviews",
  shop: "Shoppable Guides",
  compare: "Comparisons",
  product: "Products",
  products: "Products",
  guides: "Guides",
  authors: "Authors",
  "": "Main Pages",
};

function titleise(slug: string): string {
  return slug
    .replace(/[-_]+/g, " ")
    .replace(/%[0-9a-f]{2}/gi, " ")
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

export function toEntry(path: string): BrowseEntry {
  const parts = path.split("/").filter(Boolean);
  const first = parts[0] ?? "";
  const last = parts[parts.length - 1] ?? "";
  const section =
    SECTION_LABELS[first] ?? (first ? titleise(first) : "Main Pages");
  const label = last ? titleise(decodeURIComponent(last)) : "Home";
  return { path, label, section };
}

/** Fetches every indexable URL from the live sitemap (runs on the server). */
export async function fetchBrowseEntries(): Promise<BrowseEntry[]> {
  return cached("browse-entries", 60 * 60 * 1000, loadBrowseEntries);
}

async function loadBrowseEntries(): Promise<BrowseEntry[]> {
  try {
    const res = await fetch(SITEMAP_URL, {
      headers: { accept: "application/xml" },
      signal: AbortSignal.timeout(10_000),
    });
    if (!res.ok) return [];
    const xml = await res.text();
    const locs = Array.from(xml.matchAll(/<loc>([^<]+)<\/loc>/g)).map((m) =>
      m[1].trim()
    );
    const seen = new Set<string>();
    const entries: BrowseEntry[] = [];
    for (const loc of locs) {
      let path = loc.replace(/^https?:\/\/[^/]+/, "") || "/";
      if (path !== "/" && path.endsWith("/")) path = path.slice(0, -1);
      if (path === "/" || path.startsWith("/auth") || path.startsWith("/admin"))
        continue;
      if (seen.has(path)) continue;
      seen.add(path);
      entries.push(toEntry(path));
    }
    entries.sort(
      (a, b) => a.section.localeCompare(b.section) || a.path.localeCompare(b.path)
    );
    return entries;
  } catch {
    return [];
  }
}

export function paginate(entries: BrowseEntry[], page: number) {
  const totalPages = Math.max(1, Math.ceil(entries.length / BROWSE_PAGE_SIZE));
  const current = Math.min(Math.max(1, page), totalPages);
  const slice = entries.slice(
    (current - 1) * BROWSE_PAGE_SIZE,
    current * BROWSE_PAGE_SIZE
  );
  const grouped = new Map<string, BrowseEntry[]>();
  for (const e of slice) {
    const list = grouped.get(e.section) ?? [];
    list.push(e);
    grouped.set(e.section, list);
  }
  return { current, totalPages, groups: Array.from(grouped.entries()) };
}
